import { deepmerge } from "deepmerge-ts";
import React, {
  ComponentPropsWithoutRef,
  ElementRef,
  ForwardedRef,
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  DefaultTheme,
  StyledComponentProps,
  ThemeProvider,
  useTheme,
} from "styled-components";
import { PropsWithPropStyling } from "../../shared";
import { AsProp } from "../../shared/types";
import { ButtonStyle } from "../../themes/components/defaultButtonTheme";
import { Ripple, useRipple } from "../Ripple/Ripple";
import { Addon } from "./Elements/Addon";
import { Container } from "./Elements/Container";
import {
  HoverAnimation,
  HoverAnimationVariant,
} from "./Elements/HoverAnimation";
import { Label } from "./Elements/Label";
import { Spinner } from "./Elements/Spinner";

export type ButtonBaseProps<
  As extends AsProp,
  LabelAs extends AsProp
> = PropsWithChildren<{
  addonHoverEffect?: ButtonAddonHoverEffect;
  append?: ReactNode;
  appendedHoverEffect?: ButtonAddonHoverEffect;
  as?: As;
  busy?: boolean;
  disabled?: boolean;
  labelAs?: LabelAs;
  labelProps?: StyledComponentProps<
    LabelAs,
    DefaultTheme,
    PropsWithPropStyling,
    never
  >;
  hoverEffect?: ButtonHoverEffect;
  prepend?: ReactNode;
  prependedHoverEffect?: ButtonAddonHoverEffect;
  shape?: ButtonShape;
  size?: ButtonSize;
  theme?: Partial<ButtonStyle>;
  variant?: ButtonVariant;
}>;

export type ButtonProps<
  As extends AsProp = any,
  LabelAs extends AsProp = any
> = PropsWithPropStyling<
  ButtonBaseProps<As, LabelAs> & ComponentPropsWithoutRef<As>
>;

export type ButtonAddonHoverEffect = "none" | HoverAnimationVariant;

export type ButtonHoverEffect = "none" | "color" | HoverAnimationVariant;

export type ButtonShape = "normal" | "circle";

export type ButtonSize = "xs" | "s" | "m" | "l" | "xl";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "flat"
  | "accent"
  | "success"
  | "alert"
  | "danger";

const ButtonWithRef = forwardRef(Button) as <
  As extends AsProp = "button",
  LabelAs extends AsProp = "span"
>(
  props: ButtonProps<As, LabelAs> & {
    ref?: ForwardedRef<ElementRef<As>>;
  }
) => ReturnType<typeof Button>;

export { ButtonWithRef as Button };

function Button<As extends AsProp, LabelAs extends AsProp>(
  {
    addonHoverEffect,
    append,
    appendedHoverEffect,
    as: asProp,
    busy = false,
    children,
    disabled = false,
    labelAs,
    labelProps,
    hoverEffect: hoverEffectProp,
    prepend,
    prependedHoverEffect,
    shape = "normal",
    size: sizeProp,
    theme: customTheme,
    variant: variantProp,
    ...containerProps
  }: ButtonProps<As, LabelAs>,
  ref: ForwardedRef<ElementRef<As>>
) {
  const as = disabled ? "button" : asProp;

  if (addonHoverEffect) {
    appendedHoverEffect = appendedHoverEffect ?? addonHoverEffect;
    prependedHoverEffect = prependedHoverEffect ?? addonHoverEffect;
  }

  appendedHoverEffect = appendedHoverEffect ?? "none";
  prependedHoverEffect = prependedHoverEffect ?? "none";

  const [containerRef, setContainerRef] = useState<ElementRef<As> | null>(null);

  useImperativeHandle(ref, () => containerRef!, [containerRef]);

  const theme = useTheme();
  const {
    components: { button: buttonTheme, ripple: rippleTheme },
  } = theme;

  const hoverEffect = hoverEffectProp ?? buttonTheme.defaultProps!.hoverEffect!;
  const size = sizeProp ?? buttonTheme.defaultProps!.size!;
  const variant = variantProp ?? buttonTheme.defaultProps!.variant!;

  const currentButtonStyle = useMemo(() => {
    const style = deepmerge(
      buttonTheme.defaultStyle ?? {},
      buttonTheme.size?.[size] ?? {},
      buttonTheme.shape?.[shape] ?? {},
      buttonTheme.variant?.[variant] ?? {},
      customTheme ?? {}
    ) as ButtonStyle;
    if (hoverEffect !== "color") {
      style.hover = undefined;
    }
    return style;
  }, [
    buttonTheme,
    customTheme && JSON.stringify(customTheme),
    hoverEffect,
    shape,
    size,
    variant,
  ]);

  const currentTheme = useMemo(
    () =>
      deepmerge(theme, {
        components: {
          button: { current: currentButtonStyle },
          ripple: deepmerge(rippleTheme, currentButtonStyle.ripple),
        },
      } as Partial<DefaultTheme>) as DefaultTheme,
    [currentButtonStyle, rippleTheme, theme]
  );

  const { rippleProps } = useRipple({
    trigger: containerRef as HTMLElement | null,
  });

  const contentOpacity = busy ? "0" : "1";
  const withAddon = Boolean(prepend || append);
  const addonHoverEffectDelay = hoverEffect.startsWith("anim-")
    ? "0.75s"
    : undefined;

  return (
    <ThemeProvider theme={currentTheme}>
      <Container
        as={as as undefined}
        data-busy={busy || undefined}
        disabled={disabled || busy || undefined}
        withAddon={withAddon}
        {...containerProps}
        ref={setContainerRef}
      >
        {!disabled && rippleProps && <Ripple {...rippleProps} />}
        {busy && <Spinner />}
        <HoverAnimation variant={hoverEffect}>
          {prepend && (
            <Addon
              $opacity={contentOpacity}
              hoverEffect={prependedHoverEffect}
              hoverEffectDelay={addonHoverEffectDelay}
            >
              {prepend}{" "}
            </Addon>
          )}
          <Label
            $opacity={contentOpacity}
            as={labelAs as undefined}
            withAddon={withAddon}
            {...labelProps}
          >
            {children}
          </Label>
          {append && (
            <Addon
              $opacity={contentOpacity}
              hoverEffect={appendedHoverEffect}
              hoverEffectDelay={addonHoverEffectDelay}
            >
              {" "}
              {append}
            </Addon>
          )}
        </HoverAnimation>
      </Container>
    </ThemeProvider>
  );
}
